import { useUpdateRecoveryConfigMutation } from 'api/recoveryAi'
import type { RecoveryConfig } from 'api/recoveryAi.types'
import { useCallback } from 'react'
import type { WizardState } from './WizardContext'
import type { StepSlug } from './WizardStepper'

// The PUT endpoint only accepts refund_handling === 'deny' in this phase — the
// AI never grants cash refunds. The wizard therefore always submits 'deny'
// regardless of any stale state, so an 'allow' value can never reach the wire.
const REFUND_HANDLING: RecoveryConfig['refund_handling'] = 'deny'

// Parse user-entered numeric input with an explicit fallback for invalid
// values. `Number(raw) || fallback` would silently swallow a deliberate 0,
// so we use Number.isFinite as the gate. Callers pass the parser they
// want — parseInt for integer fields (thresholds), Number for fields that
// allow decimals (dollar amounts).
const parseFinite = (
  raw: string,
  fallback: number,
  parser: (s: string) => number
): number => {
  const parsed = parser(raw)
  return Number.isFinite(parsed) ? parsed : fallback
}

const parseInteger = (raw: string) => parseInt(raw, 10)

// Parse an escalation-threshold input to a non-negative integer, falling back
// to the wizard default when the value is invalid or negative. Exported so the
// Settings Escalation Rules pane (AI-33) applies the identical clamp — keeping
// one source of truth for the rule.
export const clampNonNegativeThreshold = (
  raw: string,
  fallback: number
): number => {
  const parsed = parseFinite(raw, fallback, parseInteger)
  return parsed < 0 ? fallback : parsed
}

// Wizard default for both escalation thresholds (frequent + repeated). Shared
// with the Settings pane so the clamp fallback matches. AI-1126 (FR-21): the
// blueprint default is 3 complaints (frequent + consecutive). The nexus DB
// column default is seeded separately — see AI-1126 for the companion migration.
export const DEFAULT_ESCALATION_THRESHOLD = 3

// AI-302 (FR-41): default for the suspected-gaming / auto-resolution threshold,
// used by the Settings EscalationRulesPane clamp + hydration fallback. Value is
// TBC-Darshan — match the nexus config-column default once the companion change
// lands. Kept separate from DEFAULT_ESCALATION_THRESHOLD so the gaming default
// can diverge without touching the frequent/repeated fallback.
export const DEFAULT_AUTO_RESOLUTION_THRESHOLD = 3

/**
 * Maps wizard state to the config fields for a specific R12 stage. Only the
 * Escalations stage persists in this slice:
 *  - train: the assessment/scoring stage — sets recommendedPosture in memory
 *    but persists nothing directly (the posture moves to a WP2 tuning modal).
 *  - testing: deferred placeholder stage — no persist (Testing hosts the WP2
 *    modals later).
 *  - locations: no config-PUT persist — the Locations stage (LocationsStep)
 *    persists its all-vs-subset choice via the dedicated
 *    PUT recovery/location_configs, which the config PUT does NOT accept (AI-161).
 *  - escalations: the final stage. Its Finalize action persists the escalation
 *    routing fields AND flips onboarding_completed. Finalize does NOT activate —
 *    that happens only on the ActivationPricingModal CTA.
 */
// Finalize never activates: it builds ONLY the public config-PUT body, which
// never carries operating_mode (see updateRecoveryConfig's body type).
// Activation is the ActivationPricingModal CTA's job (activateRecovery), not
// Finalize's.
function mapStepToConfig(
  step: StepSlug,
  state: WizardState
): Partial<Omit<RecoveryConfig, 'operating_mode'>> {
  switch (step) {
    case 'train':
      // Train persists its posture immediately so the Testing stage's config card
      // — which reads the SAVED config — shows what the assessment just chose
      // rather than a stale value. Finalize still re-sends these fields
      // (harmlessly, now that they agree).
      //
      // ...but ONLY for first-time onboarding. A RE-ENTERING already-activated
      // merchant must not have their LIVE agent's posture mutated by Train's
      // Continue before Finalize — no confirmation, no undo, real customer-facing
      // blast radius (Darshan review, #3878). For them Train persists nothing and
      // Finalize (which deliberately avoids downgrading a running agent) owns the
      // write. The Testing card seeds fine either way — it reads the live config,
      // which for a re-entry merchant already holds their real posture.
      if (state.alreadyOnboarded) return {}
      return {
        philosophy_posture: state.philosophyPosture,
        // Same conditional Finalize uses: Train is skippable, and sending a null
        // recommendation would clear a previously-saved one on re-entry.
        ...(state.recommendedPosture !== null && {
          philosophy_recommended_posture: state.recommendedPosture,
        }),
      }
    case 'testing':
      return {}
    case 'locations':
      // Locations persists via the dedicated PUT recovery/location_configs,
      // registered into continueActionRef and run by the wizard shell's
      // Continue handler (AI-1130) — NOT the config PUT, which rejects
      // location_configs. This case stays a config-PUT no-op by design (AI-161).
      //
      // Wizard/index.tsx's persistStep() only suppresses the generic submit
      // banner for this step because THIS returns {} (a guaranteed-successful
      // no-op submitStep call) — if locations ever needs real config-PUT
      // fields, that guard (`if (currentSlug !== 'locations')`) must be
      // revisited too, or a real submitStep failure here would go unreported.
      return {}
    case 'escalations':
      return {
        // AI-173: Finalize persists the live approved-reward selection. ENG-983
        // removed the in-flow reward selector that used to live on this step
        // (the wizard's on-step RewardSelectionCard, which pruned any since-
        // deactivated id from this selection once the catalog loaded) — the
        // selection is now only ever populated via hydration from a
        // previously-saved config's approved_reward_ids. This mapper still
        // round-trips whatever is in state.selectedRewardIds unchanged,
        // INCLUDING an empty array: product decision (2026-08) makes [] an
        // intended, terminal first-time state — rewards are chosen post-
        // activation in Settings → Approved Rewards, not required to Finalize
        // (see useActivationCompleteness). No client-side prune is needed
        // either way: nexus's PUT (services/merchant-api/.../recovery/
        // configuration.rb) re-scopes any submitted ids to the merchant's
        // OWN state: 'published' templates before persisting, so a stale or
        // since-deactivated id can never stick server-side even if this array
        // is stale.
        approved_reward_ids: state.selectedRewardIds,
        // D1/L16: the free-text address list is RETIRED — recipients are
        // merchant users on the escalation_contacts resource now. Sent as an
        // explicit [] rather than omitted: this PUT is a partial MERGE
        // (api/recoveryAi.ts), so omitting the key would silently preserve every
        // address a merchant typed pre-deploy, with no UI left to view or clear
        // them. DATA-AFFECTING by design — re-entering the wizard clears them.
        escalation_emails: [],
        // L17: kept. notify_admins defaults true with no UI control anywhere —
        // it is the always-on backstop, which is exactly why the Finalize gate
        // does NOT count it as a leg. Omitting it here would let the default
        // overwrite a merchant's saved false.
        notify_admins: state.notifyAdmins,
        refund_handling: REFUND_HANDLING,
        // The Train stage computes the recovery-philosophy posture into wizard
        // state but R12 dropped its dedicated persist step, so Finalize carries
        // it through — otherwise a completed assessment is silently discarded.
        // (Interim until the WP2 tuning modals own posture — see AI-173.)
        philosophy_posture: state.philosophyPosture,
        // Only send the recommendation when one was computed (all 3 questions
        // answered). Train is skippable, leaving recommendedPosture null; sending
        // null risks clearing a previously-saved value on re-entry, so omit it.
        ...(state.recommendedPosture !== null && {
          philosophy_recommended_posture: state.recommendedPosture,
        }),
        // R12: completing the Escalations stage is the in-app launch signal —
        // it flips the onboarding flag, which Landing reads to swap GetStarted
        // for the live dashboard.
        onboarding_completed: true,
      }
    default:
      return {}
  }
}

// Finalize only ever saves settings + flips onboarding_completed via the
// public config PUT. It never activates — activation happens exclusively on
// the ActivationPricingModal CTA (see useActivateRecoveryMutation).
export function useWizardSubmit() {
  const [
    updateConfig,
    { isLoading: isConfigSaving, isError: isConfigError, error: configError },
  ] = useUpdateRecoveryConfigMutation()

  const submitStep = useCallback(
    async (step: StepSlug, state: WizardState) => {
      const payload = mapStepToConfig(step, state)
      if (Object.keys(payload).length > 0) {
        await updateConfig(payload).unwrap()
      }
    },
    [updateConfig]
  )

  return {
    submitStep,
    isSubmitting: isConfigSaving,
    isError: isConfigError,
    error: configError,
  }
}

export { mapStepToConfig }
